/* istanbul ignore file */
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';
import type { ProductPriceProps } from '@packages/shared';
import { ProductPrice } from '@packages/shared';
import type { ProductPrice as PriceProps } from '@packages/gql/generated/shopping/graphql';
import { defineMessages, useIntl } from 'react-intl';

// eslint-disable-next-line @typescript-eslint/naming-convention
const ProductPriceFragment = graphql(/* GraphQL */ `
  fragment ProductPriceFragment on ProductPrice {
    amount
    currency
    isStartPrice
    saving {
      type
      value
    }
    unit
    uvp
    value
    valueOld
    valueUnit
  }
`);

type PricesProps = Pick<ProductPriceProps, 'showLegalHints' | 'alignment' | 'small'> & {
  priceData: FragmentType<typeof ProductPriceFragment>;
};

const messages = defineMessages({
  priceUVP: {
    id: 'price.uvp',
    defaultMessage: 'UVP:',
  },
  priceDiscount: {
    id: 'price.discount',
    defaultMessage: 'statt',
  },
  dicountLabel: {
    id: 'price.discount.label',
    defaultMessage: 'Sie sparen',
  },
});

// TODO(api-layer): this mapper function will be unnecessary after implementation of the new reusable ProductPrice component
const getPriceProps = (
  price: Partial<PriceProps>,
  labels: Partial<ProductPriceProps>,
): ProductPriceProps => ({
  ...price,
  currency: price.currency,
  price: price.value!,
  unit: price.unit || undefined,
  unitPrice: price.valueUnit || undefined,
  amount: price.amount || undefined,
  isStartPrice: price.isStartPrice || undefined,
  oldPrice: price.valueOld || undefined,
  discountType: price.saving
    ? (price.saving.type.toLocaleLowerCase() as 'percent' | 'currency')
    : undefined,
  hasDiscountFlag: !!price.saving,
  saving: price.saving?.value,
  ...labels,
});

// TODO(api-layer): this component will be unnecessary after implementation of the new reusable ProductPrice component

export const Price = ({ priceData, ...productPriceProps }: PricesProps) => {
  const { formatMessage } = useIntl();
  const price = getFragmentData(ProductPriceFragment, priceData);
  const uvpLabel = formatMessage(messages.priceUVP);
  const discountLabel = formatMessage(messages.priceDiscount);
  // TODO(core): statt vs UVP => rename uvpLabel in ProductPrice
  const label = price.uvp ? uvpLabel : discountLabel;
  const discountInfoLabel = formatMessage(messages.dicountLabel);

  const labels = {
    uvpLabel: label.length > 0 ? label : undefined,
    discountInfoLabel: discountInfoLabel.length > 0 ? discountInfoLabel : undefined,
  };
  const priceProps = getPriceProps(price, labels);

  return <ProductPrice {...priceProps} {...productPriceProps} />;
};
